// services/ApiService.js - do komunikacji z backendem

import axios from 'axios';
import { getToken } from '../utils/token';

// Centralna klasa do komunikacji z backend API
class ApiService {
    constructor() {
        this.baseURL = process.env.REACT_APP_BACKEND_URL;
        this.axiosInstance = axios.create({
            baseURL: this.baseURL,
            // Każda metoda ustawia swój timeout explicite
        });
    }

    // Krok 1: Upload pliku STL bezpośrednio na backend
    async uploadModelFile(file, sessionId) {
        try {
            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append('sessionId', sessionId);

            const response = await this.axiosInstance.post('/api/upload-model', formData, {
                timeout: 70000,
            });

            return response.data; // { key }
        } catch (error) {
            const err = new Error(
                error.response?.data?.error ||
                'Nie udało się przesłać pliku STL na serwer'
            );
            err.errorType = error.response?.data?.errorType || 'upload_failed';
            err.errorDetails = error.response?.data?.errorDetails || null;
            throw err;
        }
    }

    // Krok 2: Analizuj model STL (Blender może potrzebować czasu na thumbnail)
    async analyzeModel(key, sessionId) {
        try {
            const response = await this.axiosInstance.post('/api/analyze-model', {
                key,
                sessionId
            }, {
                timeout: 120000 // 2 minuty dla analizy + generowanie thumbnail
            });
            return response.data;

        } catch (error) {
            const err = new Error(
                error.response?.data?.error ||
                error.message ||
                'Nie udało się przeanalizować modelu'
            );
            err.errorType = error.response?.data?.errorType || 'analyze_failed';
            err.errorDetails = error.response?.data?.errorDetails || null;
            throw err;
        }
    }

    // Krok 3: Otrzymaj wycenę druku (PrusaSlicer może pracować do 10 minut)
    async getQuote(params) {
        try {
            const response = await this.axiosInstance.post('/api/quote', params, {
                timeout: 600000 // 10 minut dla operacji PrusaSlicer
            });
            return response.data; // { pricePerUnit, priceTotal, currency }
        }   catch (error) {
      console.error('❌ getQuote error:', {
          code: error.code,              // ECONNABORTED = axios timeout
          message: error.message,        // Szczegóły błędu
          hasResponse: !!error.response, // Czy backend odpowiedział?
          status: error.response?.status,
          data: error.response?.data
      });
                                                                                
      const err = new Error(
          error.response?.data?.error ||
          'Nie udało się obliczyć wyceny'
      );
      err.errorType = error.response?.data?.errorType || 'slicer_unknown';
      err.errorDetails = error.response?.data?.errorDetails || null;
      throw err;
  }
    }

    // Krok 3a: Szacowanie czasu realizacji dla całej sesji
    async estimateDelivery(items) {
        try {
            const response = await this.axiosInstance.post('/api/estimate-delivery', { items }, {
                timeout: 30000
            });
            return response.data; // { leadTimeDays, breakdown, message, machineBreakdown }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się obliczyć czasu realizacji'
            );
        }
    }


    // Walidacja zamówienia przed wysłaniem
    async validateOrder(order) {
        try {
            const response = await this.axiosInstance.post('/api/validate-order', order, {
                timeout: 30000
            });
            return response.data;
        } catch (error) {
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw new Error('Błąd walidacji zamówienia');
        }
    }

    // Krok 4: Wysyłka zamówienia do bazy danych
    async submitOrder(order) {
        try {
            const token = getToken();
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await this.axiosInstance.post('/api/orders', order, {
                timeout: 30000,
                headers, // ← dodajemy tylko jeśli JWT istnieje
            });
            return response.data; // { status, orderId }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się przesłać zamówienia'
            );
        }
    }



    // Inicjalizacja płatności P24
    async initPayment(orderId) {
        try {
            const token = getToken();
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await this.axiosInstance.post('/api/payment/init',
                                                           { orderId },
                                                           {
                                                               headers,
                                                               timeout: 30000
                                                           }
            );

            return response.data; // { success: boolean, paymentUrl: string, sessionId: string }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się zainicjować płatności'
            );
        }
    }

    // Sprawdź status płatności zamówienia
    async checkPaymentStatus(orderId) {
        try {
            const response = await this.axiosInstance.get(`/api/payment-status/${orderId}`, {
                timeout: 30000
            });
            return response.data; // { id, payment_status, payment_url, total, created_at }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się sprawdzić statusu płatności'
            );
        }
    }


    // Pobranie zamówień w panelu użytkownika
    async getOrders() {
        try {
            const token = getToken();
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await this.axiosInstance.get('/api/orders', {
                headers,
                timeout: 60000 // Może być dużo zamówień
            });
            return response.data; // { orders: [...] }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się pobrać zamówień'
            );
        }
    }


    // Pobiera dane adresowe zalogowanego usera (GET /api/userdata)
    async getUserData() {
        const token = getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        try {
            const response = await this.axiosInstance.get('/api/userdata', {
                headers,
                timeout: 30000
            });
            return {
                userData: response.data.data || null,
                wpEmail: response.data.wp_email || null
            };
        } catch {
            return { userData: null, wpEmail: null };
        }
    }

    // Zapisuje dane adresowe zalogowanego usera (POST /api/userdata)
    async updateUserData(userData) {
        const token = getToken();
        if (!token) {
            throw new Error('Brak autoryzacji');
        }

        try {
            const response = await this.axiosInstance.post('/api/userdata', userData, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000
            });
            return response.data;
        } catch (error) {
            throw new Error(
                error.response?.data?.error || 'Nie udało się zapisać danych użytkownika'
            );
        }
    }


    // Logowanie użytkownika z weryfikacją aktywacji konta (popup dla niezalogowanych)
    async login(email, password) {
        try {
            // Krok 1: Sprawdź dane logowania przez JWT
            const jwtResponse = await axios.post('https://geometryhustlers.pl/wp-json/jwt-auth/v1/token', {
                username: email,
                password: password
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            // Krok 2: Sprawdź status aktywacji
            const activationResponse = await axios.get(`https://geometryhustlers.pl/wp-json/wp/v2/users/activation-status?email=${encodeURIComponent(email)}`);

            if (!activationResponse.data.activated) {
                throw new Error(activationResponse.data.message || 'Konto nie zostało aktywowane');
            }

            // Sukces - zwróć token i dane użytkownika
            return {
                success: true,
                token: jwtResponse.data.token,
                user: {
                    email: email,
                    displayName: jwtResponse.data.user_display_name
                }
            };

        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Wystąpił błąd podczas logowania'
            };
        }
    }

    // Przepnij sesję gościa na zalogowanego użytkownika
    async transferGuestSession(guestSessionId, browserSession) {
        try {
            const token = getToken();
            if (!token) {
                throw new Error('User must be logged in');
            }

            const response = await this.axiosInstance.post('/api/transfer-guest-session', {
                guestSessionId,
                browserSession
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                timeout: 30000
            });

            return response.data; // { success: boolean, message: string, sessionId: string }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się przepiąć sesji gościa'
            );
        }
    }


    // Zapisz dane sesji (modele) do bazy danych
    async saveSessionData(sessionId, modelData) {
        try {
            const headers = {
                'Content-Type': 'application/json'
            };

            // Dodaj browserSession dla gości
            const browserSessionId = localStorage.getItem('browser_session_id');
            if (browserSessionId) {
                headers['x-browser-session'] = browserSessionId;
            }

            // Dodaj JWT token dla zalogowanych (jeśli istnieje)
            const token = getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await this.axiosInstance.post('/api/save-session-data', {
                sessionId,
                modelData
            }, {
                headers,
                timeout: 60000 // Może być sporo danych modeli
            });

            return response.data; // { success: boolean, sessionId: string, modelsCount: number }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się zapisać danych sesji'
            );
        }
    }

    // Załaduj dane sesji (modele) z bazy danych
    async loadSessionData(sessionId) {
        try {
            const headers = {};

            // Dodaj browserSession dla gości
            const browserSessionId = localStorage.getItem('browser_session_id');
            if (browserSessionId) {
                headers['x-browser-session'] = browserSessionId;
            }

            // Dodaj JWT token dla zalogowanych (jeśli istnieje)
            const token = getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await this.axiosInstance.get(`/api/load-session-data/${sessionId}`, {
                headers,
                timeout: 60000 // Może być sporo danych modeli
            });

            return response.data; // { success: boolean, sessionId: string, modelData: object, modelsCount: number }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się załadować danych sesji'
            );
        }
    }

    // Zmień status sesji (active → saved/ordered/expired)
    async updateSessionStatus(sessionId, status) {
        try {
            const headers = {
                'Content-Type': 'application/json'
            };

            // Dodaj browserSession dla gości
            const browserSessionId = localStorage.getItem('browser_session_id');
            if (browserSessionId) {
                headers['x-browser-session'] = browserSessionId;
            }

            // Dodaj JWT token dla zalogowanych (jeśli istnieje)
            const token = getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await this.axiosInstance.post('/api/update-session-status', {
                sessionId,
                status
            }, {
                headers,
                timeout: 30000
            });

            return response.data; // { success: boolean, sessionId: string, previousStatus: string, newStatus: string }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się zmienić statusu sesji'
            );
        }
    }

    // Utility: opóźnienie
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // Sprawdź czy użytkownik ma aktywną sesję wyceny
    async checkActiveSession(browserSessionId) {
        try {
            const headers = {};

            // Dodaj browserSession dla gości
            if (browserSessionId) {
                headers['x-browser-session'] = browserSessionId;
            }

            // Dodaj JWT token dla zalogowanych (jeśli istnieje)
            const token = getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await this.axiosInstance.get('/api/check-active-session', {
                headers,
                timeout: 30000
            });

            return response.data; // { hasActiveSession: boolean, sessionId?: string }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się sprawdzić aktywnej sesji'
            );
        }
    }

    // Utwórz nową sesję wyceny
    async createQuoteSession(browserSessionId, gaClientId = null, gaSessionId = null) {
        try {
            const headers = {};
            // Dodaj browserSession dla gości
            if (browserSessionId) {
                headers['x-browser-session'] = browserSessionId;
            }
            // Dodaj JWT token dla zalogowanych (jeśli istnieje)
            const token = getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Body z ga_client_id i ga_session_id
            const body = {};
            if (gaClientId) {
                body.ga_client_id = gaClientId;
            }
            if (gaSessionId) {
                body.ga_session_id = gaSessionId;
            }

            const response = await this.axiosInstance.post('/api/create-quote-session', body, {
                headers,
                timeout: 30000
            });
            return response.data; // { success: boolean, sessionId: string, expiresAt: string }
        } catch (error) {
            throw new Error(
                error.response?.data?.error ||
                'Nie udało się utworzyć sesji wyceny'
            );
        }
    }

    // Metoda instancyjna do mapowania kolorów (dostępna na instancji)
    mapParamsForBackend(frontendParams) {
        return {
            key: frontendParams.key,
            volume: frontendParams.volume,
            material: frontendParams.material,
            technology: frontendParams.technology,
            color: this.colorMap[frontendParams.color] || frontendParams.color,
            infill: frontendParams.technology === 'FDM' ? frontendParams.infill : undefined,
            count: frontendParams.quantity,
            printWithoutSupports: frontendParams.printWithoutSupports || false
        };
    }

    // Mapowanie kolorów frontend → backend (jako właściwość instancji)
    colorMap = {
        'czarny': 'black',
        'biały': 'white',
        'czerwony': 'red',
        'pomarańczowy': 'orange',
        //'zielony': 'green',
        'żółty': 'yellow',
        'szary': 'gray',
        'granitowy': 'granite',
        'jasno-szary': 'light-gray'
    };

    // Mapowanie kolorów backend → frontend (jako właściwość instancji)
    colorMapReverse = Object.fromEntries(
        Object.entries(this.colorMap).map(([pl, en]) => [en, pl])
    );
}




// Eksport singleton instance
const apiService = new ApiService();
export default apiService;
